import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { charitableRemainderFundsGuide } from '../content/innovative/charitableRemainderFunds';

// Move QR code generation to the top level
let qrCodeHomepage, qrCodeApply, qrCodeDonate;

// Add pageNumber as a module-level variable
let pageNumber = 1;

export const generateCharitableRemainderFundsPDF = async () => {
  try {
    console.log('Starting PDF generation for CRF Guide...');
    
    // Initialize/reset page number
    pageNumber = 1;

    // Generate QR codes with CRF-specific URLs
    try {
      qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
      qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');
      qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/crf-calculator');
      console.log('QR codes generated successfully');
    } catch (error) {
      console.error('Error generating QR codes:', error);
      throw new Error('Failed to generate QR codes');
    }

    // Create new document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4'
    });

    // Initialize constants
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 40;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 50;
    const footerHeight = 20;
    const qrSize = 25;

    // Define colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255],
      red: [220, 38, 38],
      lightBlue: [59, 130, 246]
    };

    // Move addCallToAction inside main function with access to all variables
    const addCallToAction = (yPosition) => {
        // Find the last page with content
        const totalPages = doc.internal.getNumberOfPages();
        
        let lastContentPage = totalPages;
        
        // Force CTA onto last content page
        doc.setPage(lastContentPage-1);
        
        // Position CTA exactly 120px above footer
        const ctaPosition = pageHeight - footerHeight - 100;
        
        // Add CTA box and content
        doc.setFillColor(41, 98, 255);
        doc.roundedRect(margin, ctaPosition, contentWidth, 80, 10, 10, 'F');
        
        // CTA text in white
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.text('Want to Support the Revolution?', margin + 30, ctaPosition + 35);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.text('Our causes could use your donated support. Scan to contribute today.', 
          margin + 30, ctaPosition + 65);
      
        // Add QR code
        doc.addImage(qrCodeDonate, 'PNG', 
          pageWidth - margin - 80, 
          ctaPosition + 15, 
          50, 
          50
        );
      
        // Delete page 8 if it exists and is empty
    if (doc.internal.getNumberOfPages() === 8) {
        doc.deletePage(8);
      }
        return ctaPosition;
      };
          // Validate content
    if (!charitableRemainderFundsGuide) {
      throw new Error('Guide content is missing');
    }

    console.log('Content validated, proceeding with generation...');

    // Update spacing constants
    const spacing = {
      afterHeading: 0,
      afterSectionTitle: 0,
      afterSubheading: 20,
      betweenItems: 15,
      bulletIndent: 15,
      numberIndent: 20,
      afterBulletList: 15,
      afterNumberedList: 20,
      paragraphSpacing: 15,
      lineHeight: 14, // Increased from 8 for better readability
      subIndent: 10,
      footerMargin: 120,
      betweenSections: 15,
      afterBulletPoint: 15,
      afterExample: 10,
      minimumSpaceForContent: 160
    };

    // Add processList function
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      let inNumberedList = false;
      let currentIndent = 0;
      
      lines.forEach(line => {
        // Handle bold section headers with colon
        if (line.match(/^\*\*.*\*\*:/)) {
          doc.setFont('helvetica', 'bold');
          const boldText = line.replace(/\*\*/g, '');
          doc.text(boldText, margin + currentIndent, currentY);
          doc.setFont('helvetica', 'normal');
          currentY += spacing.lineHeight;
          inNumberedList = false;
        }
        // Handle numbered items
        else if (line.match(/^\d+\./)) {
          inNumberedList = true;
          const numberPart = line.substring(0, line.indexOf('.') + 1);
          const textPart = line.substring(line.indexOf('.') + 1).trim();
          
          // Handle bold text within numbered items
          if (textPart.includes('**')) {
            const [boldPart, normalPart] = textPart.split('**: ');
            const boldText = boldPart.replace(/\*\*/g, '') + ':';
            
            doc.text(numberPart, margin + currentIndent, currentY);
            doc.setFont('helvetica', 'bold');
            const boldWidth = doc.getTextWidth(boldText);
            doc.text(boldText, margin + spacing.numberIndent, currentY);
            doc.setFont('helvetica', 'normal');
            
            if (normalPart) {
              const wrappedText = doc.splitTextToSize(normalPart, contentWidth - spacing.numberIndent - boldWidth - margin);
              wrappedText.forEach((textLine, idx) => {
                doc.text(textLine, margin + spacing.numberIndent + boldWidth + 5, currentY + (idx * spacing.lineHeight));
              });
              currentY += (wrappedText.length * spacing.lineHeight);
            }
          } else {
            const wrappedText = doc.splitTextToSize(textPart, contentWidth - spacing.numberIndent - margin);
            doc.text(numberPart, margin + currentIndent, currentY);
            wrappedText.forEach((textLine, idx) => {
              doc.text(textLine, margin + spacing.numberIndent, currentY + (idx * spacing.lineHeight));
            });
            currentY += (wrappedText.length * spacing.lineHeight);
          }
        }
        // Handle bullet points (both main and nested)
        else if (line.trim().startsWith('•')) {
          const bulletText = line.substring(1).trim();
          const indentLevel = line.indexOf('•') / 2; // Calculate indent level based on spaces
          const currentBulletIndent = spacing.bulletIndent + (indentLevel * spacing.subIndent);
          
          doc.text('•', margin + currentBulletIndent - spacing.subIndent, currentY);
          const wrappedText = doc.splitTextToSize(bulletText, contentWidth - currentBulletIndent - margin);
          wrappedText.forEach((textLine, idx) => {
            doc.text(textLine, margin + currentBulletIndent, currentY + (idx * spacing.lineHeight));
          });
          currentY += (wrappedText.length * spacing.lineHeight);
        }
        // Handle regular text
        else if (line.trim()) {
          const wrappedText = doc.splitTextToSize(line, contentWidth - currentIndent);
          wrappedText.forEach(textLine => {
            doc.text(textLine, margin + currentIndent, currentY);
            currentY += spacing.lineHeight;
          });
        }
        
        // Add appropriate spacing after each section
        if (line.trim()) {
          currentY += spacing.betweenItems;
        }
      });
      
      return currentY;
    };

    // Font sizes and styles
    const fonts = {
      title: 24,
      subtitle: 18,
      sectionHeading: 16,
      subHeading: 14,
      body: 11,
      footer: 8,
      disclaimer: 9,
      caption: 10
    };

    // Add PDF metadata for SEO
    doc.setProperties({
      title: "Charitable Remainder Funds Guide",
      subject: "Comprehensive guide to Charitable Remainder Funds, To Transform Your Wealth Into Legacy, Income, and Impact",
      author: "Cause & Impact, Inc.",
      keywords: charitableRemainderFundsGuide.keywords.join(", "),
      creator: "Cause & Impact, Inc.",
      producer: "Cause & Impact Fiscal Sponsorship Platform",
      creationDate: new Date(),
      modDate: new Date(),
      trapped: true
    });

    // Add header with SEO-optimized text
    const addHeader = () => {
      try {
        doc.setFillColor(17, 24, 39);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        
        // Brand emphasis - adjust y-positions
        doc.setTextColor(...colors.blue);
        doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 25.1, { align: 'center' });
        
        doc.setTextColor(...colors.green);
        doc.text('Cause & Impact, Inc.', pageWidth / 2, 25, { align: 'center' });

        // Subtitle - adjust font size and y-position
        doc.setFontSize(10);
        doc.setTextColor(...colors.white);
        doc.text('Leading Authority in Modern Fiscal Sponsorship Solutions', pageWidth / 2, 40, { align: 'center' });

        // QR code - adjust size and position
        doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 35, 5, 35, 35);
        console.log('Header added successfully');
      } catch (error) {
        console.error('Error adding header:', error);
        throw error;
      }
    };

    // Add footer with SEO elements
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      
      // Adjust y-position for footer text
      doc.text(
        'Cause & Impact, Inc. | The Future of Fiscal Sponsorship | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, 
        pageWidth - margin, 
        pageHeight - 5,
        { align: 'right' }
      );
    };

    // Add title page with SEO optimization
    const addTitlePage = () => {
      const titleY = 200;
      const maxWidth = pageWidth - (margin * 4);
      const lineSpacing = 50;
      
      // Title with auto-scaling and padding
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.title);
      let titleSize = fonts.title;
      let titleWidth = doc.getTextWidth(charitableRemainderFundsGuide.title);
      
      // Scale down title if needed
      while (titleWidth > maxWidth && titleSize > fonts.heading) {
        titleSize -= 1;
        doc.setFontSize(titleSize);
        titleWidth = doc.getTextWidth(charitableRemainderFundsGuide.title);
      }
      
      doc.setTextColor(...colors.blue);
      doc.text(charitableRemainderFundsGuide.title, pageWidth / 2, titleY, { 
        align: 'center',
        maxWidth: maxWidth
      });
      
      // Subtitle with increased spacing
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.subHeading);
      doc.text(charitableRemainderFundsGuide.subtitle, pageWidth / 2, titleY + lineSpacing, { 
        align: 'center',
        maxWidth: maxWidth
      });
      
      // Decorative line with more padding
      doc.setDrawColor(...colors.blue);
      doc.setLineWidth(2);
      doc.line(margin * 2, titleY + lineSpacing + 30, pageWidth - (margin * 2), titleY + lineSpacing + 30);
      
      // Metadata with increased spacing
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      doc.text(`Version: ${charitableRemainderFundsGuide.metadata.version}`, margin * 2, titleY + lineSpacing + 70);
      doc.text(`Published: ${charitableRemainderFundsGuide.metadata.publishDate}`, margin * 2, titleY + lineSpacing + 90);
    };

    // Add table of contents with SEO optimization
    const addTableOfContents = (yPosition) => {
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Table of Contents', margin, yPosition);
      yPosition += 30;
      
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
      const sections = [
        'Executive Overview',
        'Why Choose a Charitable Remainder Fund?',
        'Key Benefits',
        'Strategic Implementation',
        'Compliance and Regulatory Framework',
        'Strategic Management Fee Structure',
        'Investment Strategy and Management',
        'Legacy Planning and Impact',
        'Case Study: Transformational Impact',
        'Advanced Planning Considerations',
        'Next Steps',
        
      ];
      
      sections.forEach((section, index) => {
        doc.text(`${index + 1}. ${section}`, margin, yPosition);
        yPosition += 20;
      });
      
      return yPosition + 20;
    };

    // Add executive summary with SEO optimization
    const addExecutiveSummary = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Executive Overview', margin, yPosition);
      yPosition += 20;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
      const summary = "At Cause and Impact, we believe in empowering philanthropists with cutting-edge " +
        "strategies to achieve financial security, meaningful impact, and a lasting legacy. Our Charitable " +
        "Remainder Fund (CRF) is a premier vehicle for individuals seeking to optimize their giving while " +
        "securing income for life.\n\n" +
        "This guide provides a complete overview of how our CRF works, its benefits, compliance, and the " +
        "strategic fee structure that ensures a win-win for both donors and our organization.";
        
      const lines = doc.splitTextToSize(summary, contentWidth);
      doc.text(lines, margin, yPosition);
      
      return yPosition + (lines.length * spacing.lineHeight) + spacing.betweenSections;
    };

    // Add helper function to process bold text
    const processBoldText = (text, x, y) => {
      // Split text by ** markers
      const parts = text.split(/(\*\*.*?\*\*)/g);
      let currentX = x;
      
      parts.forEach(part => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Bold text
          doc.setFont('helvetica', 'bold');
          const boldText = part.replace(/\*\*/g, '');
          doc.text(boldText, currentX, y);
          currentX += doc.getTextWidth(boldText);
        } else if (part.trim()) {
          // Normal text
          doc.setFont('helvetica', 'normal');
          doc.text(part, currentX, y);
          currentX += doc.getTextWidth(part);
        }
      });
      
      // Reset font
      doc.setFont('helvetica', 'normal');
      return currentX;
    };

    // Update the section content handling for hybrid frameworks
    const processHybridFrameworks = (yPosition) => {
      // Title with proper bold formatting
      const titleText = "**Hybrid Entity Frameworks**:";
      doc.setTextColor(...colors.gray);
      processBoldText(titleText, margin, yPosition);
      yPosition += spacing.afterSubheading;

      // Numbered items with bold parts
      const frameworks = [
        {
          num: "1.",
          title: "**Enhanced LLC Model**:",
          points: [
            "Single-member LLC with professional management",
            "Multi-layer compliance controls",
            "Automated governance systems"
          ]
        }
        // ... other frameworks
      ];

      frameworks.forEach(framework => {
        // Number and bold title
        const titleX = processBoldText(
          `${framework.num} ${framework.title}`,
          margin,
          yPosition
        );
        yPosition += spacing.betweenItems;

        // Bullet points
        framework.points.forEach(point => {
          doc.text('•', margin + spacing.bulletIndent, yPosition);
          doc.text(point, margin + spacing.bulletIndent + spacing.subIndent, yPosition);
          yPosition += spacing.betweenItems;
        });
        
        yPosition += spacing.afterBulletList;
      });

      return yPosition;
    };

    // Update the addSectionContent function to use the new bold text processing
    const addSectionContent = (section, yPosition) => {
        
      // Section heading in green
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.green);
      doc.text(section.heading, margin, yPosition);
      

      // Section description
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
      yPosition += spacing.afterSubheading;

      // Process content parts
      const contentParts = section.text.split('\n\n');
      
      contentParts.forEach(part => {
        if (part.includes('**Hybrid Entity Frameworks**')) {
          yPosition = processHybridFrameworks(yPosition);
        }
        else if (part.startsWith('**Historical Context**:')) {
          // Historical Context section
          doc.setFont('helvetica', 'bold');
          doc.text('Historical Context:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          doc.setFont('helvetica', 'normal');
          const points = [
            'Origins in Adler & Colvin\'s foundational work',
            'Evolution of IRS interpretations',
            'Emergence of modern philanthropic needs'
          ];

          points.forEach(point => {
            doc.text('•', margin + 5, yPosition);
            doc.text(point, margin + spacing.bulletIndent, yPosition);
            yPosition += spacing.betweenItems;
          });
          yPosition += spacing.afterBulletList;
        }
        else if (part.startsWith('**Traditional Models Overview**:')) {
          // Traditional Models section
          doc.setFont('helvetica', 'bold');
          doc.text('Traditional Models Overview:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          const models = [
            { num: '1.', title: 'Direct Project (Model A)', desc: 'Complete integration with sponsor' },
            { num: '2.', title: 'Independent Contractor (Model B)', desc: 'Project leadership as contractors' },
            { num: '3.', title: 'Pre-approved Grant (Model C)', desc: 'Independent project with fiscal oversight' },
            { num: '4.', title: 'Group Exemption (Model D)', desc: 'Umbrella structure for multiple projects' },
            { num: '5.', title: 'Supporting Organization (Model E)', desc: 'Separate entity alignment' },
            { num: '6.', title: 'Technical Assistance (Model F)', desc: 'Focused administrative support' }
          ];

          models.forEach(({ num, title, desc }) => {
            // Number with proper spacing
            doc.setFont('helvetica', 'normal');
            doc.text(num, margin, yPosition);

            // Model name without parentheses
            const modelName = title.split('(')[0].trim();
            const modelCode = '(' + title.split('(')[1];
            
            // Calculate positions
            const numberWidth = doc.getTextWidth(num + ' ');
            const modelNameWidth = doc.getTextWidth(modelName + ' ');
            
            // Model name
            doc.text(modelName, margin + numberWidth, yPosition);
            
            // Model code in same style
            doc.text(modelCode, margin + numberWidth + modelNameWidth, yPosition);
            
            // Description
            doc.text(desc, margin + numberWidth + modelNameWidth + doc.getTextWidth(modelCode + ' '), yPosition);
            
            yPosition += spacing.betweenItems;
          });

          yPosition += spacing.afterNumberedList;
        }
        else if (part.startsWith('**Modern Innovation**:')) {
          // Modern Innovation section
          doc.setFont('helvetica', 'bold');
          doc.text('Modern Innovation:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          doc.setFont('helvetica', 'normal');
          const innovations = [
            'Hybrid structures combining multiple models',
            'Technology-enabled oversight systems',
            'Scaled compliance frameworks'
          ];

          innovations.forEach(innovation => {
            doc.text('•', margin + 5, yPosition);
            doc.text(innovation, margin + spacing.bulletIndent, yPosition);
            yPosition += spacing.betweenItems;
          });
          yPosition += spacing.afterBulletList;
        }
        else if (part.toLowerCase().includes('case study:')) {
          yPosition += spacing.paragraphSpacing;
          yPosition = addCaseStudyBox(part, yPosition);
          yPosition += spacing.paragraphSpacing;
        }
        else if (part.startsWith('**Advanced Control Systems**:')) {
          doc.setFont('helvetica', 'bold');
          doc.text('Advanced Control Systems:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          const systems = [
            {
              title: 'Fund Segregation',
              points: [
                'Automated restriction enforcement',
                'Real-time fund tracking',
                'Multi-layer approval workflows'
              ]
            },
            // ... other systems
          ];

          systems.forEach(system => {
            doc.setFont('helvetica', 'bold');
            doc.text(`1. **${system.title}**:`, margin, yPosition);
            yPosition += spacing.betweenItems;

            doc.setFont('helvetica', 'normal');
            system.points.forEach(point => {
              doc.text('•', margin + spacing.bulletIndent, yPosition);
              doc.text(point, margin + spacing.bulletIndent + spacing.subIndent, yPosition);
              yPosition += spacing.betweenItems;
            });
            yPosition += spacing.afterBulletList;
          });
        }
        else {
          const lines = doc.splitTextToSize(part, contentWidth);
          lines.forEach(line => {
            processBoldText(line, margin, yPosition);
            yPosition += spacing.lineHeight;
          });
          yPosition += spacing.paragraphSpacing;
        }
      });

      return yPosition + 50;
    };

    // Add case study box with styling
    const addCaseStudyBox = (text, yPosition) => {
      const boxPadding = 10;
      const boxHeight = 60;

      // Draw background with lighter color instead of alpha
      doc.setFillColor(240, 245, 255); // Light blue background
      doc.roundedRect(margin, yPosition, contentWidth, boxHeight, 5, 5, 'F');

      // Add case study content
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.caption);
      doc.setTextColor(...colors.blue);
      doc.text('CASE STUDY', margin + boxPadding, yPosition + boxPadding + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      const lines = doc.splitTextToSize(text, contentWidth - (boxPadding * 2));
      doc.text(lines, margin + boxPadding, yPosition + boxPadding + 15);

      return yPosition + boxHeight + 10;
    };

    // Add appendices with styling
    const addAppendices = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('', margin, yPosition);
      yPosition += 20;

      

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
     

      return yPosition + spacing.betweenSections;
    };

    // Page break checking with access to pageNumber
    const checkPageBreak = (yPosition, requiredSpace, doc) => {
      const safeRequiredSpace = requiredSpace + 20;
      
      if (yPosition + safeRequiredSpace > pageHeight - footerHeight ) {
        doc.addPage();
        pageNumber++;  // Now pageNumber is in scope
        addHeader(doc);
        addFooter(doc, pageNumber);
        return headerHeight + spacing.beforeSection;
      }
      return yPosition;
    };

    // Generate PDF with enhanced error handling
    try {
      console.log('Starting document generation...');
      
      // Add title page
      addHeader();
      addFooter(1);
      addTitlePage();
      console.log('Title page added');

      // Add table of contents
      doc.addPage();
      let pageNumber = 2;
      addHeader();
      addFooter(pageNumber);
      let yPosition = headerHeight + 20;
      yPosition = addTableOfContents(yPosition);
      console.log('Table of contents added');

      // Add executive summary
      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addExecutiveSummary(yPosition);
      console.log('Executive summary added');

      // Process main content with progress logging
      console.log(`Processing ${charitableRemainderFundsGuide.sections.length} sections...`);
      charitableRemainderFundsGuide.sections.forEach((section, index) => {
        console.log(`Processing section ${index + 1}: ${section.heading}`);
        if (yPosition + 100 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 24;
        }
        yPosition = addSectionContent(section, yPosition);
      });

      // Add appendices
      console.log('Adding appendices...');
      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addAppendices(yPosition);

      // Add CTA before end of document
      yPosition = checkPageBreak(yPosition, 80);
      yPosition = addCallToAction(yPosition);

      console.log('PDF generation completed successfully');
      return doc;
    } catch (error) {
      console.error('Error during PDF generation:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  } catch (error) {
    console.error('Fatal error in generateCharitableRemainderFundsPDF:', error);
    throw error;
  }
};

// Export with additional error handling
export const generateCRFGuide = async () => {
  try {
    console.log('Starting CRF Guide generation');
    const doc = await generateCharitableRemainderFundsPDF();
    if (!doc) {
      throw new Error('PDF generation failed - null document returned');
    }
    console.log('Guide generated successfully');
    return doc;
  } catch (error) {
    console.error('Error in generateCRFGuide:', error);
    throw error;
  }
}; 